import React, { useState, useMemo, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { LeadsGrouping } from 'src/pages/landlord/leads/types';
import { useLeadsPageContext } from 'src/pages/landlord/leads/provider';
import { useGroupedByPropertyLeads } from 'src/pages/landlord/leads/hooks/use-grouped-by-property-leads';
import LeadsTable from 'src/pages/landlord/leads/components/table/LeadsTable';
import LeadsNoResultsFound from 'src/pages/landlord/leads/components/LeadsNoResultsFound';
import { Flex } from 'src/components/flex';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 10;

export default function AllLeadsSection() {
    const {
        grouping,
        properties,
        leads,
        isFilterOrSearchActive,
    } = useLeadsPageContext();

    const groupedByPropertyLeads = useGroupedByPropertyLeads(leads);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const hasFilteredLeads = leads.length > 0;
    const isGroupedByProperty = grouping === LeadsGrouping.Property;

    const propertiesWithLeads = useMemo(() => {
        return (properties ?? []).filter(p => groupedByPropertyLeads.get(p.id)?.length);
    }, [properties, groupedByPropertyLeads]);

    const hasNoPropertyLeads = !!groupedByPropertyLeads.get(-1)?.length;

    // All paginated items: properties with leads + optional "No property" sentinel (-1)
    const allItems = useMemo(() => {
        return [...propertiesWithLeads.map(p => p.id), ...(hasNoPropertyLeads ? [-1] : [])];
    }, [propertiesWithLeads, hasNoPropertyLeads]);

    const numPages = Math.ceil(allItems.length / rowsPerPage);

    const currentPageItems = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return allItems.slice(start, start + rowsPerPage);
    }, [allItems, page, rowsPerPage]);

    const startIndex = allItems.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
    const endIndex = Math.min(page * rowsPerPage, allItems.length);

    // Reset page when the set of paginated items changes (filter, sort, grouping, search)
    useEffect(() => {
        setPage(1);
    }, [allItems.join(',')]);

    useEffect(() => {
        setPage(1);
    }, [rowsPerPage]);

    return (
        <>
            {!hasFilteredLeads && (
                <LeadsNoResultsFound
                    text={isFilterOrSearchActive ?
                        undefined :
                        'You currently have no leads.'}
                />
            )}

            {hasFilteredLeads && !isGroupedByProperty && (
                <LeadsTable leads={leads} />
            )}

            {hasFilteredLeads && isGroupedByProperty && (
                <>
                    <Flex justifyContent="flex-end" alignItems="center" sx={{ px: 2, py: 0.5 }}>
                        <Flex alignItems="center" columnGap={1}>
                            <Typography variant="caption" color="text.secondary">
                                {startIndex} - {endIndex} of {allItems.length} properties
                            </Typography>

                            <Divider orientation="vertical" flexItem />

                            <Select
                                value={rowsPerPage}
                                onChange={e => setRowsPerPage(Number(e.target.value))}
                                variant="standard"
                                disableUnderline
                                sx={{
                                    fontSize: '0.75rem',
                                    '& .MuiSelect-select': { py: 0 },
                                    '& .MuiSelect-icon': { top: 'calc(50% - 0.5em)' },
                                }}
                            >
                                {ROWS_PER_PAGE_OPTIONS.map(opt => (
                                    <MenuItem key={opt} value={opt} sx={{ fontSize: '0.75rem' }}>
                                        {opt} properties / page
                                    </MenuItem>
                                ))}
                            </Select>

                            <IconButton
                                size="small"
                                onClick={() => setPage(p => p - 1)}
                                disabled={page === 1}
                                sx={{ p: 0 }}
                            >
                                <ChevronLeftIcon size={20} />
                            </IconButton>

                            <IconButton
                                size="small"
                                onClick={() => setPage(p => p + 1)}
                                disabled={page === numPages || numPages === 0}
                                sx={{ p: 0 }}
                            >
                                <ChevronRightIcon size={20} />
                            </IconButton>
                        </Flex>
                    </Flex>

                    {currentPageItems.map(id => {
                        if (id === -1) {
                            return (
                                <LeadsTable
                                    key="no-property"
                                    title="No property"
                                    leads={groupedByPropertyLeads.get(-1)}
                                />
                            );
                        }

                        const property = propertiesWithLeads.find(p => p.id === id);
                        if (!property) { return null; }

                        return (
                            <LeadsTable
                                key={property.id}
                                title={property.name}
                                leads={groupedByPropertyLeads.get(property.id)}
                            />
                        );
                    })}
                </>
            )}
        </>
    );
}
